/**
 * Hook for patient data and statistics
 */

import { useMemo } from 'react'
import { trpc } from '@/trpc/client'
import { STATUS_BACKGROUNDS, isCancelledBackground } from '@/lib/constants'
import type { AppointmentStats } from '@/types/patient'

/**
 * Fetch patient by ID
 */
export function usePatient(patientId: string) {
	return trpc.patient.getById.useQuery(
		{ id: patientId },
		{
			enabled: !!patientId,
			staleTime: 60000, // Cache for 1 minute
		},
	)
}

/**
 * Fetch patient appointments for timeline
 */
export function usePatientAppointments(patientId: string) {
	return trpc.calendar.appointment.byPatient.useQuery(
		{
			patientId,
			futureOnly: false,
		},
		{
			enabled: !!patientId,
			staleTime: 30000,
		},
	)
}

/**
 * Calculate appointment statistics from appointment list
 */
export function useAppointmentStats(
	appointments: { background: string | null; cancelDate?: string | null; date: string }[],
): AppointmentStats {
	return useMemo(() => {
		const today = new Date().toISOString().split('T')[0]

		const stats: AppointmentStats = {
			total: appointments.length,
			attended: 0,
			cancelled: 0,
			noShow: 0,
			upcoming: 0,
		}

		for (const apt of appointments) {
			const bg = apt.background

			if (bg === STATUS_BACKGROUNDS.NO_SHOW || bg === '#d50000') {
				stats.noShow++
			} else if (
				bg === STATUS_BACKGROUNDS.CANCELLED ||
				bg === '#616161' ||
				isCancelledBackground(bg)
			) {
				stats.cancelled++
			} else if (apt.date >= today) {
				stats.upcoming++
			} else {
				stats.attended++
			}
		}

		return stats
	}, [appointments])
}
